import { isSupabaseConfigured, supabase } from "../lib/supabase";
import { adjustInventoryStock } from "./inventory";

const STORAGE_KEY = "almoune_b2b_sales";

export const DEVIS_STATUSES = {
  draft: "Brouillon",
  sent: "Envoyé",
  accepted: "Accepté",
  rejected: "Refusé",
};

export const FACTURE_STATUSES = {
  pending: "En attente",
  delivered: "Livrée",
  paid: "Payée",
  overdue: "En retard",
};

export const PAYMENT_METHODS = {
  cash: "Espèces",
  cb: "Carte bancaire",
  virement: "Virement",
  cheque: "Chèque",
};

export const normalizeSaleItem = (item = {}) => {
  const qty = Number(item.qty ?? item.quantity ?? 1);
  const unitCost = Number(item.unitCost ?? item.unit_cost ?? item.costPrice ?? 0);
  const salePrice = Number(item.salePrice ?? item.sale_price ?? item.price ?? 0);
  const tvaRate = Number(item.tvaRate ?? item.tva_rate ?? 0.055);
  const lineTotalHT = Number((item.lineTotalHT !== undefined ? item.lineTotalHT : (qty * salePrice)).toFixed(2));
  const lineTotalTTC = Number((item.lineTotalTTC !== undefined ? item.lineTotalTTC : (lineTotalHT * (1 + tvaRate))).toFixed(2));

  return {
    productId: item.productId || item.product_id || item.id || "",
    productName: item.productName || item.product_name || item.name || "Produit",
    qty,
    unitCost,
    salePrice,
    tvaRate,
    lineTotalHT,
    lineTotalTTC,
  };
};

export const calculateSaleTotals = (items = []) => {
  const normalizedItems = (Array.isArray(items) ? items : []).map(normalizeSaleItem);
  let totalHT = 0;
  let totalTVA = 0;

  normalizedItems.forEach((it) => {
    const ht = Number(it.lineTotalHT || 0);
    const rate = Number(it.tvaRate || 0);
    totalHT += ht;
    totalTVA += ht * rate;
  });

  totalHT = Number(totalHT.toFixed(2));
  totalTVA = Number(totalTVA.toFixed(2));
  const totalTTC = Number((totalHT + totalTVA).toFixed(2));

  return {
    items: normalizedItems,
    totalHT,
    totalTVA,
    totalTTC,
  };
};

export const generateB2BDocumentNumber = (documentType = "devis", existingSales = []) => {
  const prefix = documentType === "facture" ? "FAC" : "DEV";
  const now = new Date();
  const yearMonth = `${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, "0")}`;
  
  const sales = Array.isArray(existingSales) && existingSales.length ? existingSales : getB2BSales();
  const matching = sales.filter(
    (s) => s.documentType === documentType && (s.documentNumber || "").startsWith(`${prefix}-${yearMonth}`)
  );
  
  const nextSeq = String(matching.length + 1).padStart(3, "0");
  return `${prefix}-${yearMonth}-${nextSeq}`;
};

export const normalizeB2BSale = (s = {}) => {
  const documentType = s.documentType || s.document_type || "devis";
  const defaultStatus = documentType === "facture" ? "pending" : "draft";
  const status = s.status || defaultStatus;
  const calculated = calculateSaleTotals(s.items);

  return {
    id: s.id || `b2b-sale-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
    clientId: s.clientId || s.client_id || "",
    documentType,
    documentNumber: s.documentNumber || s.document_number || `${documentType === "facture" ? "FAC" : "DEV"}-${Date.now().toString().slice(-6)}`,
    status,
    paymentMethod: s.paymentMethod || s.payment_method || "virement",
    expectedDate: s.expectedDate || s.expected_date || null,
    deliveryDate: s.deliveryDate || s.delivery_date || null,
    items: calculated.items,
    totalHT: s.totalHT !== undefined ? Number(Number(s.totalHT).toFixed(2)) : calculated.totalHT,
    totalTVA: s.totalTVA !== undefined ? Number(Number(s.totalTVA).toFixed(2)) : calculated.totalTVA,
    totalTTC: s.totalTTC !== undefined ? Number(Number(s.totalTTC).toFixed(2)) : calculated.totalTTC,
    notes: s.notes || "",
    createdAt: s.createdAt || s.created_at || new Date().toISOString(),
  };
};

const readCache = () => {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.map(normalizeB2BSale) : [];
  } catch (err) {
    console.warn("Failed to read B2B sales from localStorage:", err);
    return [];
  }
};

const writeCache = (sales) => {
  const normalized = (Array.isArray(sales) ? sales : []).map(normalizeB2BSale);
  if (typeof window !== "undefined") {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(normalized));
    } catch (err) {
      console.warn("Failed to write B2B sales to localStorage:", err);
    }
  }
  return normalized;
};

export const getB2BSales = () => readCache();

export const loadB2BSales = async () => {
  const cached = readCache();
  if (!isSupabaseConfigured || !supabase) return cached;
  try {
    const { data, error } = await supabase
      .from("b2b_sales")
      .select("*")
      .order("created_at", { ascending: false });

    if (error || !data) {
      if (error) console.error("Failed to load B2B sales from Supabase:", error.message);
      return cached;
    }

    return writeCache(
      data.map((row) => ({
        id: row.id,
        clientId: row.client_id || "",
        documentType: row.document_type || "devis",
        documentNumber: row.document_number || "",
        status: row.status || (row.document_type === "facture" ? "pending" : "draft"),
        paymentMethod: row.payment_method || "virement",
        expectedDate: row.expected_date || null,
        deliveryDate: row.delivery_date || null,
        items: typeof row.items === "string" ? JSON.parse(row.items) : (row.items || []),
        totalHT: Number(row.total_ht) || 0,
        totalTVA: Number(row.total_tva) || 0,
        totalTTC: Number(row.total_ttc) || 0,
        notes: row.notes || "",
        createdAt: row.created_at || new Date().toISOString(),
      }))
    );
  } catch (err) {
    console.error("Failed to load B2B sales from Supabase:", err);
    return cached;
  }
};

export const saveB2BSale = async (sale) => {
  const normalized = normalizeB2BSale(sale);
  const all = readCache();
  const idx = all.findIndex((s) => s.id === normalized.id);
  const updated = idx >= 0 ? all.map((s, i) => (i === idx ? normalized : s)) : [normalized, ...all];
  const saved = writeCache(updated);

  if (!isSupabaseConfigured || !supabase) return normalized;
  try {
    await supabase.from("b2b_sales").upsert(
      {
        id: normalized.id,
        client_id: normalized.clientId,
        document_type: normalized.documentType,
        document_number: normalized.documentNumber,
        status: normalized.status,
        payment_method: normalized.paymentMethod,
        expected_date: normalized.expectedDate,
        delivery_date: normalized.deliveryDate,
        items: normalized.items,
        total_ht: normalized.totalHT,
        total_tva: normalized.totalTVA,
        total_ttc: normalized.totalTTC,
        notes: normalized.notes,
        created_at: normalized.createdAt,
      },
      { onConflict: "id" }
    );
  } catch (err) {
    console.error("Failed to save B2B sale to Supabase:", err);
  }
  return normalized;
};

export const deleteB2BSale = async (id) => {
  const all = readCache();
  const updated = all.filter((s) => s.id !== id);
  writeCache(updated);

  if (!isSupabaseConfigured || !supabase) return true;
  try {
    const { error } = await supabase.from("b2b_sales").delete().eq("id", id);
    if (error) throw error;
  } catch (err) {
    console.error("Failed to delete B2B sale from Supabase:", err);
    throw err;
  }
  return true;
};

/**
 * Deduct stock for a facture and mark it as "delivered".
 * Loops through items and calls adjustInventoryStock from ./inventory.
 *
 * @param {object|string} saleOrId - The sale object or sale ID
 * @returns {Promise<object>} The updated sale object
 */
export const deductStockForFacture = async (saleOrId) => {
  let sale = typeof saleOrId === "string" 
    ? readCache().find((s) => s.id === saleOrId) 
    : saleOrId;

  if (!sale) {
    throw new Error("Vente B2B introuvable pour la déduction de stock.");
  }

  const items = Array.isArray(sale.items) ? sale.items : [];

  for (const item of items) {
    const qty = Number(item.qty ?? item.quantity ?? 0);
    const productId = item.productId || item.id;
    if (productId && qty > 0) {
      await adjustInventoryStock(
        productId,
        -Math.abs(qty),
        `Vente B2B ${sale.documentNumber || sale.id} - ${item.productName || "Produit"}`
      );
    }
  }

  const updatedSale = {
    ...sale,
    status: "delivered",
    deliveryDate: sale.deliveryDate || new Date().toISOString().split("T")[0],
  };

  await saveB2BSale(updatedSale);
  return normalizeB2BSale(updatedSale);
};

/**
 * Convert an accepted Devis into a Facture.
 *
 * @param {object} devis - The devis object to convert
 * @returns {Promise<object>} The newly created facture
 */
export const createFactureFromDevis = async (devis) => {
  const allSales = getB2BSales();
  const factureNumber = generateB2BDocumentNumber("facture", allSales);

  const facture = {
    clientId: devis.clientId,
    documentType: "facture",
    documentNumber: factureNumber,
    status: "pending",
    paymentMethod: devis.paymentMethod || "virement",
    expectedDate: devis.expectedDate || null,
    deliveryDate: null,
    items: (devis.items || []).map((it) => ({ ...it })),
    totalHT: devis.totalHT,
    totalTVA: devis.totalTVA,
    totalTTC: devis.totalTTC,
    notes: devis.notes ? `Issu du devis ${devis.documentNumber || devis.id}\n${devis.notes}` : `Issu du devis ${devis.documentNumber || devis.id}`,
    createdAt: new Date().toISOString(),
  };

  return await saveB2BSale(facture);
};

export const generateLegalFactureNumber = (existingSales) => {
  const date = new Date();
  const yearMonth = date.getFullYear().toString() + (date.getMonth() + 1).toString().padStart(2, '0');
  const monthSales = (existingSales || []).filter(s => s.documentNumber && s.documentNumber.startsWith('FAC-' + yearMonth));
  const sequence = (monthSales.length + 1).toString().padStart(3, '0');
  return 'FAC-' + yearMonth + '-' + sequence;
};

export const cloneSaleForRepeat = (saleToClone, catalogProducts) => {
  const items = (saleToClone.items || []).map(item => {
    const catalogItem = (catalogProducts || []).find(p => p.id === item.productId);
    const currentCost = catalogItem ? (catalogItem.purchasePrice || catalogItem.costPrice || 0) : item.unitCost;
    const costChanged = Math.abs(currentCost - item.unitCost) > 0.01;
    return { ...item, unitCost: currentCost, oldUnitCost: item.unitCost, costChanged };
  });
  return {
    ...saleToClone,
    id: 'b2b-' + Date.now() + '-' + Math.random().toString(36).slice(2, 6),
    documentNumber: '',
    documentType: 'facture',
    status: 'pending',
    createdAt: new Date().toISOString(),
    date: new Date().toISOString(),
    items,
    isDelivered: false,
    isPaid: false,
    deliveryDate: null,
    paymentMethod: null
  };
};
